<template>
  <v-dialog fullscreen scrollable persistent :value="value">
    <v-card color="kbLight">
      <v-toolbar color="kbLight" max-height="64px" flat>
        <span class="text-h5 kbDarkBlueText--text text-capitalize font-weight-medium">
          {{ method === 'edit' ? $t('general.editStep') : $t('general.addStep') }}
        </span>
        <v-spacer></v-spacer>
        <v-btn color="kbDarkBlueText" icon @click="toggleModal()">
          <v-icon>clear</v-icon>
        </v-btn>
      </v-toolbar>
      <v-divider/>

      <v-card-text v-if="loadingStep">
        <LoadingComponent height="75vh"
                          :message="$t('general.loadingStep')" />
      </v-card-text>

      <v-card-text v-else-if="method === 'edit' && !loadingStep && Object.keys(sequenceStep).length === 0">
        <NotFound height="75vh" :message="$t('general.ruleNotFound')" />
      </v-card-text>

      <v-card-text v-else>
        <v-form ref="form">
          <v-row>
            <v-col cols="12" class="pb-0">
              <span class="kbDarkBlueText--text d-block text-subtitle-1 mb-1">
                {{ $t('general.enterStepName') }}
              </span>
              <v-text-field background-color="kbWhite"
                            color="kbDarkBlueText"
                            :label="$t('general.name')"
                            :rules="[rules.required]"
                            solo
                            v-model="step.name" />
            </v-col>

            <v-col cols="12" class="pb-0">
              <span class="kbDarkBlueText--text d-block text-subtitle-1 mb-1">
                {{ $t('general.enterStepTips') }}
              </span>
              <v-textarea background-color="kbWhite"
                          color="kbDarkBlueText"
                          :label="$t('general.stepTips')"
                          solo
                          v-model="step.observation" />
            </v-col>

            <v-col cols="12" class="py-0">
              <span class="kbDarkBlueText--text d-block text-subtitle-1 mb-1">
                {{ $t('general.stepType') }}
              </span>
              <v-select background-color="kbWhite"
                        color="kbDarkBlueText"
                        :items="stepTypes"
                        item-text="name"
                        item-value="value"
                        :label="$t('general.stepType')"
                        :rules="[rules.required]"
                        solo
                        v-model="step.type"/>
            </v-col>

            <v-col cols="12" class="py-0">
              <span class="kbDarkBlueText--text d-block text-subtitle-1 mb-1">
                {{ $t('general.isStepActive') }}
              </span>
              <v-switch color="kbBlue"
                        dark
                        v-model="step.active" />
            </v-col>

            <v-col cols="12" class="pb-0">
              <span class="kbDarkBlueText--text d-block text-subtitle-1 mb-1">
                {{ $t('general.timeToWaitBeforeRunningThisStep') }}:
              </span>
              <v-row>
                <v-col cols="12">
                  <TimeMeasure :time="step.goToNextStepAfter.afterStepInitIn"
                               :time-units="step.goToNextStepAfter.timeUnits"
                               @onTimeUnitsChange="step.goToNextStepAfter.timeUnits = $event"
                               @onTimeChange="step.goToNextStepAfter.afterStepInitIn = $event"
                  />
                </v-col>

                <!--<v-col cols="12" class="py-0" v-if="sequenceSteps && sequenceSteps.length > 1">
                  <div class="d-flex align-center mb-1">
                    <span class="kbDarkBlueText&#45;&#45;text d-block text-subtitle-1 mr-2">
                      {{ $t('general.pickTheStepToChangeTo') }}:
                    </span>
                    <v-tooltip bottom max-width="170px">
                      <template v-slot:activator="{ on }">
                        <v-icon color="kbSemiLightGray"
                                size="22px"
                                v-on="on">
                          mdi-information
                        </v-icon>
                      </template>
                      <span>{{ $t('general.ifNotSequenceStepPickedNote') }}</span>
                    </v-tooltip>
                  </div>
                  <v-select background-color="kbWhite"
                            color="kbDarkBlueText"
                            :label="$t('general.pickAStep')"
                            :items="sequenceSteps"
                            item-text="name"
                            item-value="id"
                            solo
                            v-model="step.goToNextStepAfter.nextStep" />
                </v-col>-->
              </v-row>
            </v-col>

            <div style="width: 100%"
                 v-if="step.type === 'email' && emails.length > 0">
              <v-col cols="12">
                <h4 class="kbDarkBlueText--text">
                  From email
                </h4>
              </v-col>

              <v-col cols="12">
                <v-select clearable
                          :items="emails"
                          item-text="email"
                          item-value="email"
                          :placeholder="$t('general.selectOutgoingEmail')"
                          solo
                          v-model="email.from">
                </v-select>
              </v-col>
            </div>

            <v-col cols="12" v-if="step.type === 'email'">
              <SendEmail :email="email"
                         :was-template-saved-or-updated="wasTemplateSavedOrUpdated"
                         @onHtmlExported="onHtmlExported"
                         @onSaveOrUpdateTemplateStatusChange="changeSaveOrUpdateTemplateStatus()"
                         @onSubjectChange="changeSubject"
                         @onSelectedTemplateChange="setEmailTemplateId" />
            </v-col>

            <v-col class="kbWhite" cols="12" v-else-if="step.type === 'sms'">
              <span class="kbDarkBlueText--text d-block text-h6 mb-1">
                {{ $t('general.writeSms') }}
              </span>
              <SmsData :sms="sms" />
            </v-col>

            <v-col cols="12" class="px-0" v-else-if="step.type === 'task'">
              <CreateTask :task="task" />
            </v-col>

            <v-col cols="12" v-else>
              <span class="kbDarkBlueText--text text-subtitle-1">
                {{ $t('general.timeToFinishTheTaskAfterRuleGetsExecuted') }}
              </span>
              <TimeMeasure :time="manualStepTime.after"
                           :time-units="manualStepTime.timeUnits"
                           @onTimeUnitsChange="manualStepTime.timeUnits = $event"
                           @onTimeChange="manualStepTime.after = $event"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions class="d-flex justify-end">
        <v-btn
          color="kbBlue"
          class="mr-2"
          outlined
          @click="toggleModal()">
          {{ $t('general.cancel') }}
        </v-btn>
        <v-btn
          color="kbBlue"
          dark
          :loading="loadingSubmit"
          @click="submit()">
          {{ method === 'edit' ? $t('general.edit') : $t('general.create') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { EventBus } from '@/eventBus.js'
import { mapState } from 'vuex'
import notifications from '@/libs/notifications'
import rules from '@/libs/rules'
import CreateTask from '@/pages/App/Sequences/widgets/CreateTask'
import LoadingComponent from '@/components/ui/LoadingComponent'
import NotFound from '@/components/ui/NotFound'
import SmsData from '@/pages/App/Communications/Sms/SmsData'
import TimeMeasure from '@/pages/App/Sequences/widgets/TimeMeasure'
import SendEmail from '@/components/widgets/EmailTemplates/SendEmail'
import authService from '@/services/authService'
export default {
  components: { CreateTask, TimeMeasure, LoadingComponent, NotFound, SendEmail, SmsData },
  computed: {
    ...mapState({
      sequenceStep: state => state.sequences.sequenceStep,
      sequenceSteps: state => state.sequences.sequenceSteps
    })
  },
  async created () {
    try {
      this.emails = await authService.getRegisteredEmails()
    } catch (e) {
      notifications.error(e.message)
    }
    this.emails = await authService.getRegisteredEmails()
  },
  data () {
    return {
      email: {
        from: '',
        html: '',
        subject: ''
      },
      emails: [],
      loadingStep: false,
      loadingSubmit: false,
      manualStepTime: {
        after: 0,
        timeUnits: 'minutes'
      },
      rules,
      sms: {
        message: ''
      },
      step: {
        active: true,
        observation: '',
        goToNextStepAfter: {
          afterStepInitIn: 0,
          timeUnits: 'minutes',
          nextStep: ''
        },
        name: '',
        type: ''
      },
      stepTypes: [
        { name: this.$t('general.automaticEmail'), value: 'email' },
        { name: this.$t('general.call'), value: 'call' },
        { name: this.$t('general.sms'), value: 'sms' },
        { name: this.$t('general.social'), value: 'social' },
        { name: this.$t('general.manualEmail'), value: 'mEmail' },
        { name: this.$t('general.task'), value: 'task' }
      ],
      task: {
        after: 0,
        observation: '',
        name: '',
        timeUnits: 'minutes'
      },
      wasTemplateSavedOrUpdated: false
    }
  },
  methods: {
    changeSaveOrUpdateTemplateStatus () {
      this.wasTemplateSavedOrUpdated = true
    },
    changeSubject (subject) {
      this.email.subject = subject
    },
    clearStepForm () {
      this.$refs.form.reset()
      this.step = {
        active: true,
        goToNextStepAfter: {
          afterStepInitIn: 0,
          timeUnits: 'minutes',
          nextStep: ''
        },
        name: '',
        type: ''
      }
      this.email = {
        from: '',
        html: '',
        subject: '',
        templateId: ''
      }
      this.manualStepTime = {
        after: 0,
        timeUnits: 'minutes'
      }
      this.sms = {
        message: ''
      }
      this.task = {
        after: 0,
        observations: '',
        name: '',
        timeUnits: 'minutes'
      }
    },
    async createStep () {
      const validateStepResponse = this.validateStep()
      if (typeof validateStepResponse === 'string') return notifications.error(validateStepResponse)
      try {
        this.loadingSubmit = true
        let payload
        if (this.step.type === 'task' ||
          this.step.type === 'email' ||
          this.step.type === 'sms') {
          payload = { ...this[this.step.type] }
        } else {
          payload = { ...this.manualStepTime }
        }
        let { goToNextStepAfter, ...all } = this.step
        const { afterStepInitIn, nextStep, timeUnits } = goToNextStepAfter
        if (!nextStep) goToNextStepAfter = { afterStepInitIn, timeUnits }
        else goToNextStepAfter = { afterStepInitIn, nextStep, timeUnits }
        await this.$store.dispatch('createSequenceStep', {
          appId: this.$store.getters.appUuid,
          sequenceId: this.$route.params.sequenceId,
          data: { goToNextStepAfter, payload, ...all }
        })
        this.loadingSubmit = false
        notifications.success(this.$t('general.stepSuccessfullyCreated'))
        this.$emit('getSteps')
        this.toggleModal()
      } catch (e) {
        this.loadingSubmit = false
        notifications.error(e)
      }
    },
    getHtmlTemplate () {
      EventBus.$emit('onExportHtml')
    },
    async getStep () {
      try {
        await this.$store.dispatch('getSequenceStep',
          {
            appId: this.$store.getters.appUuid,
            sequenceId: this.$route.params.sequenceId,
            stepId: this.selectedStepId
          }
        )
        this.setLocalStep()
      } catch (e) {
        notifications.error(e)
      }
    },
    async getSteps () {
      try {
        await this.$store.dispatch('getSequenceSteps',
          {
            appId: this.$store.getters.appUuid,
            sequenceId: this.$route.params.sequenceId
          }
        )
      } catch (e) {
        notifications.error(e)
      }
    },
    async getStepsAndOrSelectedStep (isDialogVisible) {
      const functions = []
      if (isDialogVisible) {
        functions.push(this.getSteps())
        if (this.method === 'edit') {
          functions.push(this.getStep())
        }
      }
      try {
        this.loadingStep = true
        await Promise.all(functions)
        this.loadingStep = false
      } catch (e) {
        this.loadingStep = false
        notifications.error(e)
      }
    },
    onHtmlExported (html) {
      this.email.html = html
      this.method === 'edit' ? this.updateStep() : this.createStep()
    },
    setEmailTemplateId (templateId) {
      this.email.templateId = templateId
    },
    setLocalStep () {
      const { payload, ...all } = this.sequenceStep
      if (this.sequenceStep.type === 'task' ||
          this.sequenceStep.type === 'email' ||
          this.sequenceStep.type === 'sms') {
        this[this.sequenceStep.type] = { ...payload }
      } else {
        this.manualStepTime = { ...payload }
      }
      this.step = { ...all }
    },
    submit () {
      if (this.step.type === 'email') {
        this.getHtmlTemplate()
      } else {
        this.method === 'edit' ? this.updateStep() : this.createStep()
      }
    },
    toggleModal () {
      this.$emit('input', false)
      this.clearStepForm()
    },
    async updateStep () {
      const validateStepResponse = this.validateStep()
      if (typeof validateStepResponse === 'string') return notifications.error(validateStepResponse)
      try {
        this.loadingSubmit = true
        let payload
        if (this.step.type === 'task' ||
          this.step.type === 'email' ||
          this.step.type === 'sms') {
          payload = { ...this[this.step.type] }
        } else {
          payload = { ...this.manualStepTime }
        }
        let { goToNextStepAfter, info, ...all } = this.step
        const { afterStepInitIn, nextStep, timeUnits } = goToNextStepAfter
        if (!nextStep) goToNextStepAfter = { afterStepInitIn, timeUnits }
        else goToNextStepAfter = { afterStepInitIn, nextStep, timeUnits }
        await this.$store.dispatch('updateSequenceStep', {
          appId: this.$store.getters.appUuid,
          sequenceId: this.$route.params.sequenceId,
          stepId: this.selectedStepId,
          data: { goToNextStepAfter, payload, ...all }
        })
        this.loadingSubmit = false
        notifications.success(this.$t('general.stepSuccessfullyUpdated'))
        this.$emit('getSteps')
        this.toggleModal()
      } catch (e) {
        this.loadingSubmit = false
        notifications.error(e)
      }
    },
    validateStep () {
      if (!this.$refs.form.validate()) return this.$t('general.youMustFillAllFieldsCorrectly')
      if (this.step.type === 'sms' && rules.required(this.sms.message) !== true) {
        return this.$t('general.smsFieldIsRequired')
      } else if (this.step.type === 'email' && (rules.required(this.email.html) !== true || rules.required(this.email.subject) !== true)) {
        return this.$t('general.emailFieldsAreRequired')
      }
      return true
    }
  },
  props: {
    method: {
      type: String,
      default: 'create'
    },
    selectedStepId: {
      type: String,
      default: ''
    },
    value: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    value (value) {
      this.getStepsAndOrSelectedStep(value)
    }
  }
}
</script>
